import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Divider,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import './Header.scss';
import seniSmartLogo from '/assets/images/logos/seni-smart-logo.svg';
import AccountSettingsModal from '../../modals/AccountSettingsModal/AccountSettingsModal.tsx';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '../../context/BreakpointContext.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import AleaLanguageSwitcher from '../AleaLanguageSwitcher/AleaLanguageSwitcher.tsx';

interface HeaderProps {
  isAccountSetupModalOpen?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isAccountSetupModalOpen = false }) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const defaultZIndexValue = 1401;
  const menuListStyle = {
    w: isMobile ? '290px' : '373px',
    pt: '25px',
    pb: '0',
    borderRadius: '20px',
    mt: '15px',
    mr: isMobile ? '5px' : '-10px',
  };

  const menuItemStyle = {
    h: '75px',
    borderBottom: '1px solid',
    borderColor: '#DCDCDC',
    justifyContent: 'center',
    fontSize: '20px',
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isAccountSettingsModalOpen, setIsAccountSettingsModalOpen] =
    useState(false);

  const [headerZIndex, setHeaderZIndex] = useState(defaultZIndexValue);

  // Drop header z-index when any global modal is opened
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<boolean>).detail;
      setHeaderZIndex(detail ? 0 : defaultZIndexValue);
    };
    window.addEventListener('alea-global-modal-open', handler as EventListener);
    return () => window.removeEventListener('alea-global-modal-open', handler as EventListener);
  }, []);

  // If AccountSetupModal is open, set header z-index to 0
  useEffect(() => {
    if (isAccountSetupModalOpen) {
      setHeaderZIndex(0);
    } else {
      setHeaderZIndex(defaultZIndexValue);
    }
  }, [isAccountSetupModalOpen]);

  // If AccountSettingsModal is open, set header z-index to 0
  useEffect(() => {
    if (isAccountSettingsModalOpen) {
      setHeaderZIndex(0);
    } else if (!isAccountSetupModalOpen) {
      setHeaderZIndex(defaultZIndexValue);
    }
  }, [isAccountSettingsModalOpen, isAccountSetupModalOpen]);

  /**
   * Handles opening the menu
   */
  const handleMenuOpen = () => {
    setIsMenuOpen(true);
    setHeaderZIndex(defaultZIndexValue);
  };

  /**
   * Handles closing the menu
   */
  const handleMenuClose = () => {
    setIsMenuOpen(false);
    setHeaderZIndex(defaultZIndexValue);
  };

  /**
   * Handles opening the account settings modal
   */
  const handleAccountSettingsClick = () => {
    setIsAccountSettingsModalOpen(true);
    setHeaderZIndex(0);
    handleMenuClose();
  };

  /**
   * Handles closing the account settings modal
   */
  const handleAccountSettingsModalClose = () => {
    setIsAccountSettingsModalOpen(false);
  };

  /**
   * Handles logout action
   */
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <Flex
        justifyContent="space-between"
        backgroundColor="white"
        height={76}
        mb="15px"
        pl={isMobile ? '90px' : '0'}
        pt="15px"
        pb="15px"
        pr="25px"
        position="relative"
        boxShadow="-1px -1px 2px 0px #FFF, 1px 1px 6.537px 0px rgba(174, 174, 192, 0.40)"
        zIndex={headerZIndex}
      >
        <Flex alignItems="center">
          <Flex alignItems="center" w={isMobile ? '114px' : '340px'} pl={isMobile ? '0' : '25px'}>
            <Link to="/">
              <svg width="123" height="55" viewBox="0 0 123 55" fill="none" xmlns="http://www.w3.org/2000/svg" style={{maxHeight: "56px"}}>
                <path d="M69.5147 24.2843C70.7831 25.5528 71.4201 27.088 71.4201 28.8899V34.6334C71.4201 34.8457 71.3439 35.0253 71.1914 35.1778C71.039 35.3302 70.8593 35.4064 70.647 35.4064H70.6089C70.3966 35.4064 70.2169 35.3302 70.0645 35.1778C69.9121 35.0253 69.8359 34.8457 69.8359 34.6334V33.207C68.1863 34.6715 66.5422 35.401 64.9036 35.401C63.1016 35.401 61.5664 34.764 60.298 33.4956C59.0295 32.2271 58.3926 30.6919 58.3926 28.8899C58.3926 27.088 59.0295 25.5528 60.298 24.2843C61.5664 23.0159 63.1016 22.3789 64.9036 22.3789C66.7056 22.3789 68.2408 23.0159 69.5092 24.2843H69.5147ZM61.414 25.4058C60.4559 26.3748 59.9768 27.5344 59.9768 28.8899C59.9768 30.2455 60.4559 31.4159 61.414 32.385C62.383 33.3431 63.548 33.8222 64.909 33.8222C66.27 33.8222 67.4351 33.3431 68.3932 32.385C69.3622 31.4159 69.8468 30.2509 69.8468 28.8899C69.8468 27.5289 69.3622 26.3748 68.3932 25.4058C67.4405 24.4367 66.2809 23.9522 64.909 23.9522C63.5372 23.9522 62.383 24.4367 61.414 25.4058Z" fill="black"/>
                <path d="M75.2584 17.2012C75.4707 17.2012 75.6504 17.2719 75.8028 17.4135C75.9553 17.5659 76.0315 17.751 76.0315 17.9688V34.6274C76.0315 34.8397 75.9553 35.0194 75.8028 35.1718C75.6504 35.3242 75.4707 35.4005 75.2584 35.4005H75.2203C75.008 35.4005 74.8283 35.3242 74.6759 35.1718C74.5235 35.0194 74.4473 34.8397 74.4473 34.6274V17.9688C74.4473 17.751 74.5235 17.5659 74.6759 17.4135C74.8283 17.2719 75.008 17.2012 75.2203 17.2012H75.2584Z" fill="black"/>
                <path d="M91.106 26.1882C91.1985 26.3842 91.2094 26.5801 91.1332 26.7816C91.057 26.9775 90.9209 27.1191 90.7303 27.2116C89.5544 27.7397 88.0029 28.442 86.0866 29.3076C83.7076 30.3855 82.0145 31.1477 81.0128 31.5941C82.1887 33.1075 83.5824 33.8642 85.1883 33.8642C86.2554 33.8642 87.2244 33.5485 88.09 32.917C88.9229 32.3127 89.5054 31.5233 89.843 30.5434C89.9682 30.1895 90.224 30.0153 90.5997 30.0153C90.8664 30.0153 91.0842 30.1242 91.2421 30.3419C91.3945 30.5597 91.4217 30.7938 91.3292 31.0497C90.8828 32.3344 90.1043 33.3797 88.9937 34.18C87.8505 34.9966 86.5766 35.4049 85.1829 35.4049C83.3809 35.4049 81.8457 34.7679 80.5773 33.4995C79.3088 32.231 78.6719 30.6958 78.6719 28.8938C78.6719 27.0919 79.3088 25.5567 80.5773 24.2882C81.8457 23.0198 83.3809 22.3828 85.1829 22.3828C86.4786 22.3828 87.6654 22.7367 88.7433 23.4444C89.794 24.1358 90.5833 25.0504 91.1005 26.1936L91.106 26.1882ZM85.1883 23.9779C83.8273 23.9779 82.6623 24.4624 81.6933 25.4315C80.7352 26.4005 80.2561 27.5655 80.2561 28.9265C80.2561 29.1715 80.2996 29.5798 80.3813 30.1514L89.2768 26.1283C88.0846 24.6965 86.7181 23.9833 85.1883 23.9833V23.9779Z" fill="black"/>
                <path d="M104.771 24.2843C106.039 25.5528 106.676 27.088 106.676 28.8899V34.6334C106.676 34.8457 106.6 35.0253 106.447 35.1778C106.295 35.3302 106.115 35.4064 105.903 35.4064H105.865C105.652 35.4064 105.473 35.3302 105.32 35.1778C105.168 35.0253 105.092 34.8457 105.092 34.6334V33.207C103.442 34.6715 101.798 35.401 100.159 35.401C98.3575 35.401 96.8223 34.764 95.5538 33.4956C94.2854 32.2271 93.6484 30.6919 93.6484 28.8899C93.6484 27.088 94.2854 25.5528 95.5538 24.2843C96.8223 23.0159 98.3575 22.3789 100.159 22.3789C101.961 22.3789 103.497 23.0159 104.765 24.2843H104.771ZM96.6699 25.4058C95.7117 26.3748 95.2326 27.5344 95.2326 28.8899C95.2326 30.2455 95.7117 31.4159 96.6699 32.385C97.6389 33.3431 98.8039 33.8222 100.165 33.8222C101.526 33.8222 102.691 33.3431 103.649 32.385C104.618 31.4159 105.103 30.2509 105.103 28.8899C105.103 27.5289 104.618 26.3748 103.649 25.4058C102.696 24.4367 101.537 23.9522 100.165 23.9522C98.793 23.9522 97.6389 24.4367 96.6699 25.4058Z" fill="black"/>
                <path d="M25.0627 11.3667C20.898 13.5824 19.3029 18.8032 21.5186 22.9679L30.8007 40.4322C33.0164 44.5969 38.2372 46.192 42.4018 43.9763C46.5665 41.7606 48.1616 36.5452 45.9459 32.3805L36.6638 14.9162C34.4481 10.7515 29.2273 9.15644 25.0627 11.3721" fill="#7454FF"/>
                <path d="M19.2273 23.491C18.7864 22.6581 18.3345 21.0249 18.242 19.3862C18.1712 18.1232 18.3399 17.5081 18.5632 16.1089C18.6557 15.5428 17.8827 15.2815 17.6159 15.7877C15.5526 19.6639 13.1954 24.0844 11.2573 27.7264C10.1848 29.7407 8.95449 31.7278 8.32843 33.3773C7.88203 34.5532 7.79492 35.5114 7.79492 36.3987C7.79492 41.1241 11.6493 44.973 16.3747 44.973C21.1001 44.973 24.9762 41.1132 24.9381 36.3878C24.9163 33.9762 23.9745 32.3974 22.9674 30.5464C21.1763 27.2583 19.2219 23.491 19.2219 23.491" fill="#7454FF"/>
                <path d="M110.225 17.9054H110.728C110.894 17.9054 111.027 17.9403 111.127 18.01C111.227 18.0796 111.277 18.1885 111.277 18.3366V18.3431C111.277 18.452 111.249 18.5369 111.192 18.5979C111.14 18.6589 111.07 18.7024 110.983 18.7286L111.362 19.3361H111.035L110.689 18.7808H110.539V19.3361H110.225V17.9054ZM110.722 18.5652C110.887 18.5652 110.97 18.4956 110.97 18.3562V18.3497C110.97 18.2756 110.948 18.2234 110.905 18.1929C110.861 18.1624 110.8 18.1471 110.722 18.1471H110.539V18.5652H110.722ZM110.722 19.8457C110.892 19.8457 111.048 19.8174 111.192 19.7608C111.336 19.6998 111.46 19.617 111.564 19.5125C111.673 19.4036 111.758 19.2751 111.819 19.1271C111.88 18.979 111.911 18.8157 111.911 18.6371C111.911 18.4585 111.88 18.2952 111.819 18.1471C111.763 17.9991 111.682 17.8728 111.578 17.7682C111.473 17.6594 111.347 17.5766 111.199 17.52C111.055 17.459 110.896 17.4285 110.722 17.4285C110.548 17.4285 110.386 17.459 110.238 17.52C110.095 17.5766 109.97 17.6594 109.866 17.7682C109.761 17.8771 109.679 18.0056 109.618 18.1537C109.561 18.3018 109.533 18.4651 109.533 18.6436C109.533 18.8222 109.563 18.9855 109.624 19.1336C109.685 19.2817 109.768 19.408 109.873 19.5125C109.981 19.617 110.108 19.6998 110.251 19.7608C110.399 19.8174 110.556 19.8457 110.722 19.8457ZM110.722 20.0809C110.521 20.0809 110.332 20.0438 110.153 19.9698C109.979 19.8958 109.827 19.7956 109.696 19.6693C109.565 19.5386 109.461 19.3862 109.383 19.212C109.309 19.0378 109.271 18.8483 109.271 18.6436C109.271 18.4433 109.309 18.256 109.383 18.0818C109.457 17.9033 109.559 17.7486 109.69 17.618C109.82 17.4873 109.973 17.385 110.147 17.3109C110.325 17.2326 110.517 17.1934 110.722 17.1934C110.926 17.1934 111.116 17.2304 111.29 17.3044C111.469 17.3785 111.623 17.4808 111.754 17.6115C111.885 17.7421 111.987 17.8967 112.061 18.0753C112.135 18.2495 112.172 18.4368 112.172 18.6371C112.172 18.8418 112.133 19.0334 112.054 19.212C111.98 19.3862 111.878 19.5386 111.747 19.6693C111.617 19.7956 111.462 19.8958 111.284 19.9698C111.109 20.0438 110.922 20.0809 110.722 20.0809Z" fill="black"/>
                <text fill="black" xmlSpace="preserve" style={{whiteSpace: "pre", fontFamily: "Poppins, sans-serif", fontSize: "10px", fontWeight: "300", letterSpacing: "0em"}}>
                  <tspan x="75.1582" y="48.2455">Cockpit</tspan>
                </text>
              </svg>
            </Link>
          </Flex>
          <Divider orientation="vertical" color="#DCDCDC" sx={{ borderRadius: '4px' }} height="50px" />
          <Flex alignItems="center" ml="25px">
            <Text fontSize="10px" mr="10px">
              {t('exclusively_with')}
            </Text>
            <Link to="#">
              <Image src={seniSmartLogo} alt="Seni Smart Logo" width="82px" />
            </Link>
          </Flex>
        </Flex>

        <Flex alignItems="center">
          {!isMobile && (
            <Text fontSize="20px" mr="24px">
              {user
                ? `${user.first_name} ${user.last_name}`
                : t('general.loading')}
            </Text>
          )}
          <Menu isOpen={isMenuOpen} onClose={handleMenuClose}>
            <MenuButton onClick={handleMenuOpen} data-testid="header-profile-menu">
              {user && user.image && (
                <Avatar
                  name={
                    user ? `${user.first_name} ${user.last_name}` : ''
                  }
                  src={user && user.image ? user.image : undefined}
                  width="50px"
                  height="50px"
                />
              )}

              {user && !user.image && (
                <Avatar
                  name={
                    user ? `${user.first_name} ${user.last_name}` : ''
                  }
                  width="50px"
                  height="50px"
                />
              )}
            </MenuButton>
            <MenuList {...menuListStyle}>
              <Box textAlign="center" pb="30px">
                {user && user.image && (
                  <Avatar
                    name={
                      user
                        ? `${user.first_name} ${user.last_name}`
                        : 'User Avatar'
                    }
                    src={user && user.image ? user.image : undefined}
                    width="150px"
                    height="150px"
                    mb="30px"
                  />
                )}

                {user && !user.image && (
                  <Avatar
                    name={
                      user
                        ? `${user.first_name} ${user.last_name}`
                        : ''
                    }
                    width="150px"
                    height="150px"
                    mb="30px"
                  />
                )}
                <Box mt="25px" fontSize="25px" fontWeight="500" mb="5px">
                  {user ? `${user.first_name} ${user.last_name}` : t('general.loading')}
                </Box>
                <Box fontSize="15px" fontWeight={300} color="#2B4956">
                  {user?.position ? t(user.position) : ''}
                </Box>

                {/*language switcher*/}
                <AleaLanguageSwitcher />
              </Box>
              <MenuItem
                as="a"
                href="#"
                borderTop="1px solid"
                {...menuItemStyle}
                onClick={handleAccountSettingsClick}
                data-testid="header-account-settings"
              >
                {t('accountSettings')}
              </MenuItem>
              <MenuItem
                as="a"
                href="mailto:support@assistme.io"
                {...menuItemStyle}
              >
                {t('support')}
              </MenuItem>
              <MenuItem
                as="a"
                href="#"
                {...menuItemStyle}
                color="#EF5C36"
                borderBottomLeftRadius="20px"
                borderBottomRightRadius="20px"
                borderBottom={0}
                _hover={{ backgroundColor: 'red.100' }}
                onClick={handleLogout}
                data-testid="header-logout"
              >
                {t('logOut')}
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>

      {/*menu modal - only for backdrop*/}
      {isMenuOpen && (
        <Modal isOpen={isMenuOpen} onClose={handleMenuClose}>
          <ModalOverlay bg="blackAlpha.500" />
        </Modal>
      )}

      {/*account settings modal*/}
      <AccountSettingsModal
        isOpen={isAccountSettingsModalOpen}
        onClose={handleAccountSettingsModalClose}
      />
    </>
  );
};

export default Header;
